/*@flow*/
import React, { useEffect, useState, useRef } from 'react';
import { set } from 'ops';
import PATHS from 'const/paths';
import * as I from 'immutable';
import Legendario from 'comps/Legendario';
import useContextState from 'comps/Left2/useContextState';
import useCommands from 'comps/Left2/useCommands';
import SearchAddress from './SearchAddress';
import ToggleSidebarBtn2 from './ToggleSidebarBtn2';
import basemaps from 'lib/basemaps';
import getMapCenter from 'lib/getMapCenter';
import useStoreState from 'lib/useStoreState';
import waitTill from 'lib/waitTill';
import usePrev from 'lib/usePrev';
import updateFilterQuery from 'lib/updateFilterQuery';
import * as db from 'lib/db';
import getQueryKey from 'lib/getQueryKey';
import getBboxCenter from 'lib/getBboxCenter';
import same from 'lib/same';
import useCachedQuery from 'lib/useCachedQuery';
import GetMyCompanyCenter from './queries/GetMyCompanyCenter';
import useFilterLocalStorageSync from './useFilterLocalStorageSync';
import useBrowserQuery from './useBrowserQuery';

function fitMapToBoundingBox(map, maxZoom) {
  const center = getMapCenter();
  if (!center) return;
  map.flyTo(center, maxZoom);
}

function useMapCenter() {
  const { data, ...rest } = useCachedQuery(GetMyCompanyCenter);
  const d = I.getIn(data, ['me', 'company', 'center']);
  return { ...rest, data: d };
}

function useDefaultDistrictCenter() {
  const [dCenter, setCenter] = useState('');
  const { data: districtCenterWkt } = useMapCenter();

  useEffect(() => {
    if (!districtCenterWkt) return;
    const matched = districtCenterWkt.match(/POINT\s*\((.+) (.+)\)/);
    const center = [matched[2], matched[1]].map((v) => parseFloat(v));
    setCenter(center);
  }, [districtCenterWkt]);

  return dCenter;
}

function useMap() {
  const s = useContextState();
  const cmd = useCommands();
  const [lonlat, setLonlat] = useState([]);
  const prevLonLat = usePrev(lonlat);
  const [readyMap, setMap] = useState();
  const storeState = useStoreState({
    started: '$queryInitialized',
    zoom: '$query.mapConfig.zoom',
  });
  const { zoom } = storeState;
  const query = useBrowserQuery();

  useEffect(() => {
    if (lonlat.length === 0) return;
    if (same(prevLonLat, lonlat)) return;
    const [lng, lat] = lonlat;
    lng && lat && cmd.mapClicked(lat, lng);
  }, [lonlat, s && s.activePanel]);

  useEffect(() => {
    if (!prevLonLat || lonlat) return;
    setLonlat([]);
  }, [prevLonLat, lonlat]);

  function onMapReady(e) {
    const { map } = e;
    waitTill(() => map).then(() => setMap(map));
  }

  function createMarker(map, markerPos) {
    const [lon, lat] = markerPos.split(',').map(parseFloat);
    map.flyTo([lat, lon]);
    cmd.putMarkerAt(lat, lon);
    cmd.openLocationPanelOnLargeScreens();
  }

  useEffect(() => {
    if (!readyMap) return;
    const markerPos = getQueryKey('marker');
    if (typeof markerPos === 'string') {
      createMarker(readyMap, markerPos);
    }
  }, [readyMap]);

  const [zoomInitialized, setZoomInit] = useState(false);
  useEffect(() => {
    if (zoomInitialized) return;
    if (!readyMap) return;
    if (!zoom) return;
    cmd.set('schema.zoom', zoom);
    fitMapToBoundingBox(readyMap, zoom);
    setZoomInit(true);
  }, [readyMap, zoomInitialized, zoom]);

  const schema = {
    type: 'map',
    center: getMapCenter(),
    zoom: 15,
    maxZoom: 22,
    scrollWheelZoom: true,
    doubleClickZoom: false,
    on: {
      viewreset: () => {
        updateFilterQuery();
      },
      dragend: () => {
        updateFilterQuery();
      },
      click: ({ e }) => {
        const { lat, lng } = e.latlng;
        setLonlat([lng, lat]);
      },
      zoomend: () => {
        updateFilterQuery();
      },
    },
    ready: onMapReady,
  };

  const decks = {
    basemap: {
      url: basemaps[db.read('basemap') || 'quiet'],
      type: 'vector',
      access_token: ENV.MAPBOX_ACCESS_TOKEN,
    },
    zoom: {
      type: 'zoom',
      position: 'topright',
    },
    drawPoly: {
      type: 'draw',
      active: false,
      ready(handler: Object) {
        set(PATHS.drawHandler, handler);
      },
      // onVertexAdd(handler) {},
      // onVertexDragEnd(handler) {},
    },
    searchPanel: {
      type: 'control',
      content: () => <SearchAddress />,
      position: 'topright',
      className: 'search-control',
    },
    toggleSidebar2: {
      type: 'control',
      content: () => <ToggleSidebarBtn2 />,
      position: 'topright',
      className: 'toggle-sidebar-control',
    },
    legend: {
      type: 'control',
      content: () => <Legendario />,
      position: 'bottomright',
    },
  };

  useEffect(() => {
    cmd.set('schema', I.fromJS(schema));
    cmd.set('decks', I.fromJS(decks));
  }, []);

  const dCenter = useDefaultDistrictCenter();
  const mapInit = useRef();

  useEffect(() => {
    const mapInitialized = mapInit.current;
    if (mapInitialized) return;
    const bbox = query.getIn(['filter', 'bbox']);
    let newCenter;
    if (bbox) {
      const center = getBboxCenter(bbox);
      newCenter = center;
    } else {
      newCenter = dCenter;
    }
    if (!newCenter) return;
    mapInit.current = true;
    cmd.set('schema.center', newCenter);
  }, [dCenter, query]);

  useFilterLocalStorageSync();
}

export default useMap;
